import React, { Component } from 'react';
import { Box, Paper, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { connect } from 'react-redux';
import Currency from '../../../commonModules/Currency';

class PriceEvolutionChart extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            error: null,
            priceData: null,
            selectedMetric: 'priceU_TTC' // Par défaut: Prix Unitaire TTC
        };
    }

    componentDidMount() {
        if (this.props.rfid && this.props.historyId) {
            this.loadPriceEvolution();
        }
    }

    componentDidUpdate(prevProps) {
        if ((this.props.rfid !== prevProps.rfid || this.props.historyId !== prevProps.historyId) &&
            this.props.rfid && this.props.historyId) {
            this.loadPriceEvolution();
        }
    }

    async loadPriceEvolution() {
        const { rfid, historyId } = this.props;
        
        if (!rfid || !historyId) {
            return;
        }

        this.setState({ loading: true, error: null });

        try {
            const url = `/api/rfid/price/evolution/${encodeURIComponent(rfid)}?history_id=${encodeURIComponent(historyId)}`;
            const response = await fetch(url, {
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const contentType = response.headers.get('content-type');
            const isJson = contentType && contentType.includes('application/json');
            
            let data;
            if (isJson) {
                const responseText = await response.text();
                try {
                    data = JSON.parse(responseText);
                } catch (parseError) {
                    console.error('Failed to parse JSON:', parseError, 'Response:', responseText);
                    throw new Error(`Erreur de parsing JSON: ${parseError.message}`);
                }
            } else {
                const text = await response.text();
                throw new Error(`Réponse inattendue (${contentType || 'unknown'}): ${text.substring(0, 200)}`);
            }

            if (!response.ok) {
                const errorMessage = data.error || `Erreur HTTP ${response.status}`;
                throw new Error(errorMessage);
            }

            if (!data || !data.path || !Array.isArray(data.path)) {
                throw new Error('Format de données invalide');
            }

            // Transformer les données pour le graphique
            const chartData = data.path
                .filter((point) => point.step !== 0) // Exclure le nœud 0 du début
                .map((point, index) => {
                    const priceU_TTC = point.quantity > 0 ? point.price_ttc / point.quantity : 0;
                    const pru = point.quantity > 0 ? point.cost / point.quantity : 0; // Prix de Revient Unitaire
                    return {
                        step: point.step,
                        name: `${point.op_type}\n${point.date.substring(0, 10)}`,
                        fullName: `${point.op_type} - ${point.shop_name} - ${point.date}`,
                        date: point.date,
                        opType: point.op_type,
                        shopName: point.shop_name,
                        priceU: point.price_u || 0,
                        priceU_TTC: priceU_TTC,
                        priceHT: point.price_ht || 0,
                        priceTTC: point.price_ttc || 0,
                        cost: point.cost || 0,
                        pru: pru, // Prix de Revient Unitaire
                        quantity: point.quantity
                    };
                });

            this.setState({ priceData: chartData, loading: false });
        } catch (error) {
            console.error('Error loading price evolution:', error);
            this.setState({ 
                error: error.message || 'Erreur lors du chargement de l\'évolution des prix',
                loading: false 
            });
        }
    }

    formatCurrency(value) {
        const currency = this.props.currency || 'Euro';
        const numValue = parseFloat(value) || 0;
        
        let formattedValue;
        let symbol;
        
        if (currency === 'Euro') {
            formattedValue = (Math.round(numValue * 100) / 100).toFixed(2);
            symbol = '€';
        } else {
            // Pour FCFA, afficher aussi 2 décimales
            formattedValue = (Math.round(numValue * 100) / 100).toFixed(2);
            symbol = 'FCFA';
        }
        
        return `${formattedValue} ${symbol}`;
    }

    handleMetricChange = (event) => {
        this.setState({ selectedMetric: event.target.value });
    }

    getMetricConfig() {
        const { selectedMetric } = this.state;
        
        const metrics = {
            'priceU': {
                dataKey: 'priceU',
                name: 'Prix Unitaire HT',
                stroke: '#2196F3',
                label: 'Prix Unitaire HT'
            },
            'priceU_TTC': {
                dataKey: 'priceU_TTC',
                name: 'Prix Unitaire TTC',
                stroke: '#4CAF50',
                label: 'Prix Unitaire TTC'
            },
            'pru': {
                dataKey: 'pru',
                name: 'Prix de Revient Unitaire (PRU)',
                stroke: '#9C27B0',
                label: 'Prix de Revient Unitaire'
            },
            'cost': {
                dataKey: 'cost',
                name: 'Coût',
                stroke: '#FF9800',
                label: 'Coût'
            }
        };

        return metrics[selectedMetric] || metrics['priceU_TTC'];
    }

    render() {
        const { loading, error, priceData, selectedMetric } = this.state;
        const { rfid, historyId } = this.props;

        if (!rfid || !historyId) {
            return null;
        }

        if (loading) {
            return (
                <Paper style={{ padding: 16, marginTop: 16 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                        <Typography variant="body1" style={{ marginLeft: 16 }}>
                            Chargement de l'évolution des prix...
                        </Typography>
                    </Box>
                </Paper>
            );
        }

        if (error) {
            return (
                <Paper style={{ padding: 16, marginTop: 16 }}>
                    <Alert severity="error">{error}</Alert>
                </Paper>
            );
        }

        if (!priceData || priceData.length === 0) {
            return (
                <Paper style={{ padding: 16, marginTop: 16 }}>
                    <Alert severity="info">
                        Aucune donnée d'évolution de prix disponible.
                    </Alert>
                </Paper>
            );
        }

        const metricConfig = this.getMetricConfig();

        return (
            <Paper style={{ padding: 16, marginTop: 16 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Évolution des Prix depuis l'Origine
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            RFID: <strong>{rfid}</strong> | Nœud sélectionné: <strong>{historyId}</strong>
                        </Typography>
                    </Box>
                    <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                        <InputLabel id="metric-select-label">Métrique</InputLabel>
                        <Select
                            labelId="metric-select-label"
                            id="metric-select"
                            value={selectedMetric}
                            onChange={this.handleMetricChange}
                            label="Métrique"
                        >
                            <MenuItem value="priceU">Prix Unitaire HT</MenuItem>
                            <MenuItem value="priceU_TTC">Prix Unitaire TTC</MenuItem>
                            <MenuItem value="pru">Prix de Revient Unitaire (PRU)</MenuItem>
                            <MenuItem value="cost">Coût</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box mt={2}>
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart
                            data={priceData}
                            margin={{
                                top: 10,
                                right: 30,
                                left: 0,
                                bottom: 60,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="name" 
                                angle={-45}
                                textAnchor="end"
                                height={100}
                                interval={0}
                            />
                            <YAxis />
                            <Tooltip 
                                formatter={(value) => {
                                    return this.formatCurrency(value);
                                }}
                                labelFormatter={(label, payload) => {
                                    if (payload && payload.length > 0) {
                                        return payload[0].payload.fullName;
                                    }
                                    return label;
                                }}
                                contentStyle={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px'
                                }}
                            />
                            <Legend />
                            <Line 
                                type="monotone" 
                                dataKey={metricConfig.dataKey}
                                stroke={metricConfig.stroke}
                                name={metricConfig.name}
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>

                {/* Tableau détaillé */}
                <Box mt={3}>
                    <Typography variant="subtitle1" gutterBottom>
                        Détails par Étape
                    </Typography>
                    <Box style={{ maxHeight: 300, overflowY: 'auto', marginTop: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Étape</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Type</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Shop</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Qté</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Prix U HT</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Prix U TTC</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>PRU</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Prix HT</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Prix TTC</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Coût</th>
                                </tr>
                            </thead>
                            <tbody>
                                {priceData.map((point, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '8px' }}>{point.step}</td>
                                        <td style={{ padding: '8px' }}>{point.date.substring(0, 10)}</td>
                                        <td style={{ padding: '8px' }}>{point.opType}</td>
                                        <td style={{ padding: '8px' }}>{point.shopName}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{point.quantity}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{this.formatCurrency(point.priceU)}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{this.formatCurrency(point.priceU_TTC)}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{this.formatCurrency(point.pru)}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}><Currency value={point.priceHT} /></td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}><Currency value={point.priceTTC} /></td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}><Currency value={point.cost} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            </Paper>
        );
    }
}

// Map Redux state to props
function mapStateToProps(state) {
    return {
        currency: state.currency?.currency || 'Euro'
    };
}

export default connect(mapStateToProps)(PriceEvolutionChart);
